import type { WizardStep } from '@/components/wizards';
import { isAtPlatformFloor } from '@/hooks/useMSRPCalculator';
import { MINIMUM_SUBSCRIPTION_COST } from '@/sections/customerManagement/constants';

import type { WizardFormData } from './AddCustomerModal.types';

/**
 * Wizard step configuration for the Add Customer modal.
 * Maps existing validation logic to the generalized wizard system.
 */
export const customerWizardSteps: WizardStep<WizardFormData>[] = [
  {
    id: 'basic-info',
    order: 1,
    title: 'Basic Information',
    validate: data =>
      !!(
        data.settings_display_name &&
        data.customer_type &&
        data.profile_picture &&
        (data.allowed_domains.length > 0 || !!data.parent_account)
      ),
  },
  {
    id: 'attack-surface',
    order: 2,
    title: 'Attack Surface',
    validate: data =>
      Object.values(data.attack_surface).some(value => value === true),
  },
  {
    id: 'configuration',
    order: 3,
    title: 'Configuration',
    validate: data =>
      !!(data.subscription_start_date && data.subscription_end_date),
  },
  {
    id: 'scan-frequency',
    order: 4,
    title: 'Scan Frequency',
    // Shown for ALL customer types — NO shouldSkip.
    validate: () => true, // scan_day always has a valid default (Monday)
  },
  {
    id: 'subscription-pricing',
    order: 5,
    title: 'Subscription Pricing',
    // Only show for MANAGED/SAAS customer types
    shouldSkip: data =>
      data.customer_type !== 'MANAGED' && data.customer_type !== 'SAAS',
    validate: data => {
      // Skip validation if not MANAGED/SAAS (shouldSkip will hide this step)
      if (data.customer_type !== 'MANAGED' && data.customer_type !== 'SAAS') {
        return true;
      }
      const fieldsOk = !!(
        data.subscription_tier &&
        data.subscription_scan_schedule &&
        data.subscription_assets !== undefined &&
        data.subscription_applications !== undefined &&
        data.subscription_cost !== undefined &&
        data.subscription_cost > MINIMUM_SUBSCRIPTION_COST &&
        data.subscription_assets >= MINIMUM_SUBSCRIPTION_COST &&
        data.subscription_applications >= MINIMUM_SUBSCRIPTION_COST
      );
      // At the $13K platform floor a paid subscription must include at least
      // the mandatory Centurion Intelligence tier (mirrors the configure
      // modal's requireMinimumTier gate so the wizard can't bypass it).
      const intelligenceTier = data.intelligence_tier ?? 'none';
      const atFloor = isAtPlatformFloor({
        numberOfAssets: data.subscription_assets || 0,
        scanSchedule: data.subscription_scan_schedule || 'daily',
      });
      const intelligenceOk = !atFloor || intelligenceTier !== 'none';
      return fieldsOk && intelligenceOk;
    },
  },
  {
    id: 'praetorian-users',
    order: 6,
    title: 'Praetorian Users',
    validate: data => {
      const praetorianUsers = data.collaborators.filter(c =>
        c.collaborator.endsWith('@praetorian.com')
      );
      return praetorianUsers.length > 0;
    },
  },
  {
    id: 'customer-users',
    order: 7,
    title: 'Customer Users',
    isFinal: true,
    validate: () => true, // Customer users are optional
  },
];

/**
 * Map step ID to step component rendering.
 * Used by the modal to render the correct step content.
 */
export const STEP_ID_MAP = {
  'basic-info': 'BASIC_INFO',
  'attack-surface': 'ATTACK_SURFACE',
  configuration: 'CONFIGURATION',
  'scan-frequency': 'SCAN_FREQUENCY',
  'subscription-pricing': 'SUBSCRIPTION_PRICING',
  'praetorian-users': 'PRAETORIAN_USERS',
  'customer-users': 'CUSTOMER_USERS',
} as const;
